"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import EditableList from "@/app/(components)/generics/EditableList/EditableList";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import EditableTimeline from "@/app/(components)/generics/EditableTimeline/EditableTimeline";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanEditContent } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateAvatarField } from "@/app/actions/client-avatar/update/action";
import { useFetchAvatarSituation } from "@/app/hooks/fetchs/client-avatar/useFetchAvatarTabs";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import {
  AvatarField,
  DailyRoutineItem,
  UserClientAvatar,
} from "@/app/types/client-avatar/UserClientAvatar";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { Limits } from "@/app/vars/Limits";
import { AvatarQuerySubKey, QueryKey } from "@/app/vars/QueryKeys";
import styles from "../AvatarDetail.module.css";
import { AvatarDetailCard } from "./AvatarDetailCard/AvatarDetailCard";
import cardStyles from "./AvatarDetailCard/AvatarDetailCard.module.css";

type Props = DefaultProps & {
  avatar: UserClientAvatar;
  isLoading?: boolean;
  isAnalyzing?: boolean;
  params: { lang: string };
};

const listPlaceholder = [
  { cells: [{ width: "90%" }] },
  { cells: [{ width: "75%" }] },
  { cells: [{ width: "60%" }] },
];

const timelinePlaceholder = [
  { cells: [{ width: "40px" }, { width: "70%" }] },
  { cells: [{ width: "40px" }, { width: "55%" }] },
  { cells: [{ width: "40px" }, { width: "65%" }] },
  { cells: [{ width: "40px" }, { width: "50%" }] },
];

export default function AvatarDetailSituation(props: Props) {
  const T = useT();
  const canEdit = useCanEditContent();
  const { avatar, isLoading, isAnalyzing = false, params } = props;
  const user = useAuthUser();
  const isPlaceholder = !user;
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: situationData, isLoading: isFetching } = useFetchAvatarSituation({
    avatarId: avatar.id,
    enabled: !isPlaceholder,
  });

  const fullAvatar = { ...avatar, ...(situationData || {}) } as UserClientAvatar;
  const isLoadingTab = isLoading || isFetching;

  const handleSaveList = async (fieldKey: AvatarField, newItems: string[]) => {
    try {
      await updateAvatarField(avatar.offerId, avatar.type, fieldKey, newItems);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.SITUATION,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  const handleSaveTimeline = async (fieldKey: AvatarField, newItems: DailyRoutineItem[]) => {
    try {
      await updateAvatarField(avatar.offerId, avatar.type, fieldKey, newItems);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.SITUATION,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  const handleSaveText = async (fieldKey: AvatarField, val: string) => {
    try {
      await updateAvatarField(avatar.offerId, avatar.type, fieldKey, val);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.SITUATION,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  if (isLoadingTab) {
    return (
      <div className={styles.loaderContainer}>
        <PointsLoader
          centered
          delay={Limits.CLIENT_AVATAR_LOADER_DELAY}
          color="var(--color-green-3)"
        />
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_SITUATION_CURRENT)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.currentSituation || []}
              onSave={(items) => handleSaveList(AvatarField.CURRENT_SITUATION, items)}
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_SITUATION_DESIRED)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.desiredSituation || []}
              onSave={(items) => handleSaveList(AvatarField.DESIRED_SITUATION, items)}
              bulletColor="var(--color-analysis-signal)"
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>
      </div>

      <AvatarDetailCard
        title={T(TKey.CLIENT_AVATAR_DETAIL_SITUATION_ROUTINE)}
        isPlaceholder={isPlaceholder}
        placeholderConfig={timelinePlaceholder}
      >
        <EditableTimeline
          items={fullAvatar.dailyRoutine || []}
          onSave={(items) => handleSaveTimeline(AvatarField.DAILY_ROUTINE, items)}
          isPlaceholder={isPlaceholder}
          disabled={isAnalyzing || !canEdit}
        />
      </AvatarDetailCard>

      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_SITUATION_MINDSET)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={[{ cells: [{ width: "100%" }] }, { cells: [{ width: "80%" }] }]}
        >
          {isPlaceholder ? null : (
            <EditableText
              value={fullAvatar.mindset || ""}
              onSave={(val) => handleSaveText(AvatarField.MINDSET, val)}
              className={cardStyles.textBody}
              multiline={true}
              maxLength={Limits.MAX_AVATAR_SENTENCE}
              placeholder={T(TKey.CLIENT_AVATAR_DETAIL_SITUATION_MINDSET_PLACEHOLDER)}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_SITUATION_BLOCKERS)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.blockers || []}
              onSave={(items) => handleSaveList(AvatarField.BLOCKERS, items)}
              bulletColor="var(--color-analysis-pain)"
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>
      </div>

      {isPlaceholder && (
        <div className={styles.ctaContainer}>
          <Button
            min={false}
            type={ButtonType.PRIMARY}
            text={T(TKey.CLIENT_AVATAR_CREATE_BUTTON)}
            onClick={() => ModalStore.getState().openSignIn(params)}
          />
        </div>
      )}
    </div>
  );
}
